import * as THREE from "three";
import { entityDefinitions } from "../../data/entities";
import { FACE, G, Kit, boxUV, hdr, instance, once } from "./kit";
import { blobShadow, decalMaterial, glowSprite, printMaterial } from "./fx";
import { DISPLAY_FONT, canvasTex, rivetTex, stripesTex } from "./tex";

/**
 * The bridge (the original's Gate): a red steel truss deck over all lanes — red means you can drive on it — and the
 * dark red walls in blocked lanes. Origin = centre of the 25 m footprint on the road; front (−z) faces the player.
 */

const DECK = entityDefinitions.deck;       // 13 wide, 25 deep, 7.38–10.38 up
const WALL = entityDefinitions.gateWall;   // 4 wide, 25 deep, 0–7.38
const ROAD_HALF = 6.6;
const RED = 0xd8322a, DARK_RED = 0x93231b;

const steel = (): THREE.MeshStandardMaterial => once("mat:bridgesteel", () => {
  const normal = rivetTex().clone();
  normal.wrapS = normal.wrapT = THREE.RepeatWrapping;
  normal.repeat.set(0.5, 0.5);
  normal.needsUpdate = true;
  return new THREE.MeshStandardMaterial({ vertexColors: true, normalMap: normal, normalScale: new THREE.Vector2(0.9, 0.9), roughness: 0.42, metalness: 0.35 });
});

const deckTop = (): THREE.Texture => canvasTex("bridge-top", 256, 512, (ctx, w, h) => {
  ctx.fillStyle = "#4b4e60";
  ctx.fillRect(0, 0, w, h);
  for (let i = 0; i < 900; i++) {
    ctx.fillStyle = `rgba(${Math.random() < 0.5 ? "255,255,255" : "0,0,0"},${0.05 + Math.random() * 0.05})`;
    ctx.fillRect(Math.random() * w, Math.random() * h, 2, 2);
  }
  ctx.fillStyle = "#f2f2f8";
  const px = (x: number) => w / 2 + (x / 12.4) * w;
  for (const x of [-2, 2]) for (let y = 0; y < h; y += 128) ctx.fillRect(px(x) - 3, y + 16, 6, 64);
  for (const x of [-5.9, 5.9]) ctx.fillRect(px(x) - 3, 0, 6, h);
});

const signTex = (): THREE.Texture => canvasTex("bridge-sign", 1024, 128, (ctx, w, h) => {
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `900 104px ${DISPLAY_FONT}`;
  ctx.fillText("SOL RUN", w / 2 - 40, h / 2 + 6);
  const x = w / 2 + ctx.measureText("SOL RUN").width / 2 + 10;
  ctx.fillStyle = "#14f195";
  ctx.beginPath();
  ctx.moveTo(x, h / 2 - 18); ctx.lineTo(x + 44, h / 2 - 18); ctx.lineTo(x + 44, h / 2 - 42); ctx.lineTo(x + 92, h / 2); ctx.lineTo(x + 44, h / 2 + 42); ctx.lineTo(x + 44, h / 2 + 18); ctx.lineTo(x, h / 2 + 18);
  ctx.closePath();
  ctx.fill();
});

/** A box with UVs in metres (for the rivet normal map). */
const steelBox = (w: number, h: number, d: number): THREE.BufferGeometry => once(`g:steelbox:${w}:${h}:${d}`, () => boxUV(new THREE.BoxGeometry(w, h, d).toNonIndexed(), 1));

/** `buildBridgeDeck()` → the deck entity: girders 7.38–10.38 m, a road on top, truss sides, pillars outside the road. ~2.6k tris. */
export function buildBridgeDeck(): THREE.Group {
  return instance("bridgeDeck", () => {
    const kit = new Kit();
    const w = DECK.width, d = DECK.depth, y0 = DECK.yBase, y1 = DECK.yBase + DECK.height;
    const st = steel();
    // the box girder: bottom flange, top slab, front and back fascias, inner webs
    kit.add(steelBox(w, 0.3, d), st, DARK_RED, { p: [0, y0 + 0.15, 0] });
    kit.add(steelBox(w, 0.4, d), st, RED, { p: [0, y1 - 0.2, 0] });
    for (const z of [-1, 1]) kit.add(steelBox(w, y1 - y0, 0.34), st, RED, { p: [0, (y0 + y1) / 2, z * (d / 2 - 0.17)] });
    for (const x of [-1, 1]) kit.add(steelBox(0.12, y1 - y0 - 0.7, d - 0.7), st, DARK_RED, { p: [x * (w / 2 - 0.65), (y0 + y1) / 2, 0] });
    // Warren truss along both sides
    const chordY = [y0 + 0.32, y1 - 0.42];
    for (const x of [-1, 1]) {
      const px = x * (w / 2 - 0.22);
      for (const y of chordY) kit.add(G.box(0.44, 0.44, d), "satin", RED, { p: [px, y, 0] });
      const bays = 5;
      const bay = (d - 0.7) / bays;
      const hgt = chordY[1] - chordY[0];
      for (let i = 0; i <= bays; i++) kit.add(G.box(0.36, hgt, 0.36), "satin", RED, { p: [px, (chordY[0] + chordY[1]) / 2, -d / 2 + 0.35 + i * bay] });
      for (let i = 0; i < bays; i++) {
        const za = -d / 2 + 0.35 + i * bay;
        const len = Math.hypot(bay / 2, hgt);
        const tilt = Math.atan2(bay / 2, hgt);
        kit.add(G.box(0.3, len, 0.3), "satin", RED, { p: [px, (chordY[0] + chordY[1]) / 2, za + bay / 4], r: [tilt, 0, 0] });
        kit.add(G.box(0.3, len, 0.3), "satin", RED, { p: [px, (chordY[0] + chordY[1]) / 2, za + (3 * bay) / 4], r: [-tilt, 0, 0] });
      }
    }
    // the front's hazard band and sign, a row of lights under the front edge
    const hazard = decalMaterial("bridge-hazard", stripesTex("#ffc21a", "#17181f", 18, 1024, 64), { roughness: 0.45, cutout: true });
    kit.add(G.plane(w - 0.1, 0.42), hazard, 0xffffff, { p: [0, y0 + 0.3, -d / 2 - 0.012], r: FACE.front });
    kit.add(G.plane(w - 0.1, 0.42), hazard, 0xffffff, { p: [0, y0 + 0.3, d / 2 + 0.012], r: FACE.back });
    kit.add(G.plane(8.4, 1.05), decalMaterial("bridge-sign", signTex(), { roughness: 0.3, emissive: 0.25 }), 0xffffff, { p: [0, (y0 + y1) / 2 + 0.25, -d / 2 - 0.012], r: FACE.front });
    for (let i = -3; i <= 3; i++) kit.add(G.box(0.5, 0.08, 0.2), "glow", hdr(0xfff1c8, 1.6), { p: [i * 1.8, y0 - 0.04, -d / 2 + 0.5] });
    // the road on top: asphalt with lane marks, curbs, railings
    kit.add(G.plane(w - 0.6, d), printMaterial("bridge-top", deckTop(), { roughness: 0.9 }), 0xffffff, { p: [0, y1 + 0.006, 0], r: FACE.up });
    for (const x of [-1, 1]) {
      kit.add(G.box(0.3, 0.18, d), "paint", 0xf2f2f6, { p: [x * (w / 2 - 0.15), y1 + 0.09, 0] });
      kit.add(G.box(0.1, 0.1, d), "satin", RED, { p: [x * (w / 2 - 0.1), y1 + 1.1, 0] });
      kit.add(G.box(0.08, 0.08, d), "satin", RED, { p: [x * (w / 2 - 0.1), y1 + 0.62, 0] });
      for (let z = -d / 2 + 0.6; z <= d / 2; z += 2.4) kit.add(G.box(0.1, 1.1, 0.1), "satin", RED, { p: [x * (w / 2 - 0.1), y1 + 0.55, z] });
    }
    // pillars outside the road, arms under the deck, concrete footings
    for (const x of [-1, 1]) {
      const px = x * (ROAD_HALF + 1.3);
      for (const z of [-d / 2 + 2.2, d / 2 - 2.2]) {
        kit.add(G.chamfer(1.8, 0.5, 1.8, 0.08), "matte", 0xbab6c4, { p: [px, 0.25, z] });
        kit.add(G.chamfer(1.1, y0 - 0.5, 1.1, 0.08), "satin", RED, { p: [px, 0.5 + (y0 - 0.5) / 2, z] });
        kit.add(G.chamfer(Math.abs(px) - w / 2 + 1.0, 0.7, 1.0, 0.06), "satin", DARK_RED, { p: [x * ((Math.abs(px) + w / 2) / 2), y0 + 0.2, z] });
        kit.add(G.box(1.12, 0.6, 1.12), printMaterial("bridge-pillar-band", stripesTex("#ffc21a", "#17181f", 4, 256, 64), { roughness: 0.5 }), 0xffffff, { p: [px, 0.9, z] });
      }
    }
    const root = new THREE.Group();
    root.name = "bridgeDeck";
    root.add(kit.build({ receive: true }), blobShadow(w + 3, d + 2, 0.6));
    return root;
  });
}

const noEntry = (): THREE.Texture => canvasTex("sign-noentry", 256, 256, (ctx, w, h) => {
  ctx.beginPath(); ctx.arc(w / 2, h / 2, 124, 0, Math.PI * 2); ctx.fillStyle = "#ffffff"; ctx.fill();
  ctx.beginPath(); ctx.arc(w / 2, h / 2, 112, 0, Math.PI * 2); ctx.fillStyle = "#e5262f"; ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(w / 2 - 78, h / 2 - 20, 156, 40);
});

/**
 * `buildGateWall()` → a blocked lane under the bridge: dark red steel 4 × 7.38 × 25 m, hazard band, no-entry sign, red
 * lamps. The body is exactly the collision box (full lane width, road up to the deck's underside), so two blocked lanes
 * side by side close into one solid wall and the deck visibly rests on it.
 */
export function buildGateWall(): THREE.Group {
  return instance("gateWall", () => {
    const kit = new Kit();
    const w = WALL.width, h = WALL.height, d = WALL.depth;
    const st = steel();
    kit.add(steelBox(w, h, d), st, 0x8a2119, { p: [0, h / 2, 0] });
    // stiffeners on the front face and along the sides
    for (let i = 0; i < 5; i++) kit.add(G.box(0.16, h - 0.2, 0.14), "satin", 0x6f1912, { p: [-w / 2 + 0.35 + i * ((w - 0.7) / 4), h / 2, -d / 2 - 0.05] });
    kit.add(G.box(w - 0.1, 0.18, 0.16), "satin", 0x6f1912, { p: [0, h - 0.1, -d / 2 - 0.06] });
    for (const x of [-1, 1]) for (const y of [2.4, 4.9]) kit.add(G.box(0.12, 0.2, d - 0.2), "satin", 0x6f1912, { p: [x * (w / 2 - 0.02), y, 0] });
    // hazard band at the bottom, the no-entry sign
    const hazard = decalMaterial("wall-hazard", stripesTex("#ffc21a", "#17181f", 6, 512, 128), { roughness: 0.45, cutout: true });
    kit.add(G.plane(w - 0.1, 0.9), hazard, 0xffffff, { p: [0, 0.45, -d / 2 - 0.13], r: FACE.front });
    for (const x of [-1, 1]) kit.add(G.plane(d - 0.2, 0.9), hazard, 0xffffff, { p: [x * (w / 2 + 0.012), 0.45, 0], r: x > 0 ? FACE.left : FACE.right });
    kit.add(G.cyl(1.05, 1.05, 0.12, 36), "satin", 0xdfe2e8, { p: [0, 3.6, -d / 2 - 0.16], r: [Math.PI / 2, 0, 0] });
    kit.add(G.plane(2.0, 2.0), decalMaterial("sign-noentry", noEntry(), { roughness: 0.35, emissive: 0.15, cutout: true }), 0xffffff, { p: [0, 3.6, -d / 2 - 0.225], r: FACE.front });
    const root = new THREE.Group();
    root.name = "gateWall";
    root.userData.anim = "blink";
    root.add(kit.build(), blobShadow(w + 1, d + 1.5, 0.7));
    for (const [i, x] of [-1.4, 1.4].entries()) {
      const lamp = new THREE.Group();
      lamp.name = `lamp${i}`;
      lamp.add(new THREE.Mesh(G.hemi(0.16, 12, 5), once("mat:lampdome:ff2a3c", () => new THREE.MeshBasicMaterial({ color: hdr(0xff2a3c, 1.9), toneMapped: false }))));
      const glow = glowSprite(0xff2a3c, 1.6, 0.75);
      glow.name = "lampGlow";
      lamp.add(glow);
      lamp.position.set(x, h - 0.5, -d / 2 - 0.16);
      lamp.rotation.x = -Math.PI / 2;
      root.add(lamp);
    }
    return root;
  });
}
